import { callable } from '../callable';
import type {
  MembershipProfile,
  MembershipStage,
  CriterionStatus,
  MembershipEvidence,
  MembershipReadinessReport,
  RosterAnnex,
  Attestation,
  AttestationStatus,
  CriterionStatusCode,
  MembershipMilestoneKey,
  EvidenceKind,
} from '@dosfilos/domain';

/** One row of the doctrinal-attestation cycle view (status only, never content). */
export interface DoctrinalAttestationRow {
  personId: string;
  fullName: string;
  status: AttestationStatus;
  signedAt?: string;
}

function call<Req, Res>(name: string) {
  const fn = callable<Req, Res>(name);
  return async (data: Req): Promise<Res> => (await fn(data)).data;
}

export const membershipApi = {
  getProfile: call<{ tenantId: string }, MembershipProfile>('membershipGetProfile'),

  /** Solo la etapa OBJETIVO. `currentStage` YA NO SE ACEPTA: la etapa alcanzada
   *  la declara TMAI al aprobar una postulación, y el callable rechaza el campo
   *  de forma ruidosa si un bundle viejo lo manda. */
  setStage: call<
    {
      tenantId: string;
      targetStage: MembershipStage;
      milestones?: Partial<Record<MembershipMilestoneKey, string>>;
    },
    MembershipProfile
  >('membershipSetStage'),

  setCriterionStatus: call<
    {
      tenantId: string;
      criterionId: string;
      status: CriterionStatusCode;
      note?: string;
      evidenceIds?: string[];
    },
    CriterionStatus
  >('membershipSetCriterionStatus'),

  upsertEvidence: call<
    {
      tenantId: string;
      id?: string;
      criterionId: string;
      kind: EvidenceKind;
      storageRef?: string;
      label: string;
    },
    MembershipEvidence
  >('membershipUpsertEvidence'),

  recordAttestation: call<
    { tenantId: string; status: AttestationStatus; cycleYear: number },
    Attestation
  >('membershipRecordAttestation'),

  listAttestations: call<{ tenantId: string; cycleYear: number }, DoctrinalAttestationRow[]>(
    'membershipListAttestations',
  ),

  runReadiness: call<
    { tenantId: string; cycleYear?: number },
    { id: string; report: MembershipReadinessReport }
  >('membershipRunReadiness'),

  getReadiness: call<{ tenantId: string }, MembershipReadinessReport | null>(
    'membershipGetReadiness',
  ),

  /** Persisted manual statuses — the criteria page seeds its controls from these,
   *  not from the readiness snapshot. */
  listCriterionStatuses: call<{ tenantId: string }, CriterionStatus[]>(
    'membershipListCriterionStatuses',
  ),

  /** Persisted evidence docs (all criteria). */
  listEvidence: call<{ tenantId: string }, MembershipEvidence[]>('membershipListEvidence'),

  /** Delete an evidence file (Storage object + doc). */
  deleteEvidence: call<{ tenantId: string; evidenceId: string }, { deleted: boolean }>(
    'membershipDeleteEvidence',
  ),

  evidenceDownloadUrl: call<
    { tenantId: string; evidenceId: string },
    { url: string; expiresInSeconds: number }
  >('membershipEvidenceDownloadUrl'),

  /** Re-validate the latest snapshot (freshness gate) before rendering the
   *  aggregate/zero-PII TMAI packet PDF. Throws if the analysis changed. */
  assemblePacket: call<{ tenantId: string; readinessReportId: string }, MembershipReadinessReport>(
    'membershipAssemblePacket',
  ),

  /** Build the PII roster annex (SEPARATE artifact, tenantAdmin only). */
  buildRosterAnnex: call<{ tenantId: string }, RosterAnnex>('membershipBuildRosterAnnex'),
};
